import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

//Material UI
import { Paper, Typography, Grid } from '@mui/material';
import PaginationItem from "@mui/material/PaginationItem";

//Components
import { DataGrid, ViewButton, EditButton, CreateFab, FabContainer } from '@arredo/reactjs-ui';
import ModalNewSale from './create';
import MenuActions from './menuActions';
import OrderFilters from './filters';

//Hooks
import useOrders from '../../hooks/orders/useOrders';
import useAuthorize from '../../hooks/user/useAuthorize';
import useQueryString from '../../hooks/queryString/useQueryString';
import useStyles from './styles';
import { useLocation } from 'react-router-dom';

// Models
import { columns, title, OrderStates, CanEdit } from '../../models/OrderEntity';
import { PermissionsSalesAuthorized, Permissions } from '../../models/Roles';
import useUser from '../../hooks/user/useUser';
import { channels, subChannels } from '../../models/Channels';

const getDefaultFilters = (isFranchise = false) => ({
    pageSize: 10,
    pageNumber: 1,
    channel: isFranchise ? channels.values.Franchises : channels.values.Tienda,
    subChannel: null
})

const Orders = () => {
    const { business, branch } = useUser()
    const { classes } = useStyles();
    const isAuthorized = useAuthorize();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const { setParameterSearch, filters, search, clearFilters } = useQueryString();
    const location = useLocation();
    const isFranchiseSearch = location.pathname === '/orders/search';
    const { orders, loading, totalPage, refreshOrder, FindOrders } = useOrders(filters, !isFranchiseSearch);

    useEffect(() => {
        if (branch?.code && !isFranchiseSearch)
            setParameterSearch({ ...getDefaultFilters(business.isFranchise), branchCodes: branch.code })
    }, [branch?.code, location.pathname])

    const handleChangePage = (e, page) => {
        const updatedFilters = { ...filters, pageNumber: Number(page) };
        setParameterSearch(updatedFilters);
        if (isFranchiseSearch && (filters?.pageNumber !== 1 || page !== 1) && JSON.stringify(filters) !== "{}")
            FindOrders(null, updatedFilters)
    }

    const handleOrderChange = async (order) =>
        await refreshOrder(order);

    const showMenuActions = (order) => {
        if (isAuthorized(PermissionsSalesAuthorized))
            return false;

        if (order.state === OrderStates.Initiated ||
            order.state === OrderStates.PaymentRefused)
            return false;

        if (branch.businessId !== order.businessId)
            return true;

        if (order.state === OrderStates.PaymentInitiated ||
            order.state === OrderStates.PaymentProcessed ||
            order.state === OrderStates.InvoicedWithErrors ||
            order.state === OrderStates.PartiallyRefunded ||
            order.state === OrderStates.Invoiced)
            return true;

        return order.vouchers && !!order.vouchers.length;
    }

    return (
        <>
            <Grid container spacing={2}>
                {
                    showCreateModal &&
                    <ModalNewSale
                        open={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                    />
                }
                <Grid size={12}>
                    <Paper className={classes.paper}>
                        <Typography variant="h4">
                            {isFranchiseSearch ? "Buscador de Ventas" : title}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={12}>
                    <OrderFilters
                        filters={filters}
                        setFilters={setParameterSearch}
                        clearFilters={clearFilters}
                        franchiseSearch={isFranchiseSearch}
                        handleSearch={() => FindOrders()}
                    />
                </Grid>
                <Grid size={12}>
                    <DataGrid
                        columns={columns}
                        rows={orders}
                        loading={loading}
                        createLabel='Nueva Venta'
                        paginationProps={{
                            count: totalPage,
                            page: filters?.pageNumber || 1,
                            onChange: handleChangePage,
                            renderItem: (item) => <PaginationItem {...item} />
                        }}
                        actions={({ row }) => (
                            <>
                                <ViewButton to={`/orders/detail/${row.id}`} />
                                {
                                    !isFranchiseSearch && isAuthorized([Permissions.SalesEdit]) && CanEdit(row, branch.code) &&
                                    <EditButton to={`/orders/${row.id}`} />
                                }
                                {
                                    !isFranchiseSearch && !showMenuActions(row) &&
                                    <MenuActions
                                        order={row}
                                        onChange={handleOrderChange}
                                        branches={branch}
                                    />
                                }
                            </>
                        )}
                    />
                </Grid>
            </Grid>
            <FabContainer>
                {!isFranchiseSearch && isAuthorized([Permissions.SalesCreate]) && <CreateFab title='Nueva venta' onClick={() => setShowCreateModal(true)} />}
            </FabContainer>
        </>
    );
}

export default Orders;